import { inject, Injectable } from '@angular/core';
import { Collaborator } from '@control-tower/features/collaborators/models/collaborator.model';
import { CollaboratorsService } from '@control-tower/features/collaborators/services/collaborators.service';
import { AuthState } from '@core/store/auth/auth.state';
import { Store } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';

// TODO: get ridoff this these cache service, use apollo cache or service worker caching

export interface CollaboratorContext {
  userType: 'DEFAULT' | 'INTERNAL' | 'EXTERNAL' | 'GUEST';
  effectiveScopes: string[];
  collaborator: Collaborator | null;
  isFeatureMarketingCTACompleted?: boolean;
}

interface CachedContext {
  context: CollaboratorContext;
  timestamp: number;
}

const CACHE_TTL_MS = 59 * 60 * 1000;

@Injectable({
  providedIn: 'root',
})
export class CollaboratorContextCacheService {
  private readonly store = inject(Store);
  private readonly collaboratorsService = inject(CollaboratorsService);

  private cache = new Map<string, CachedContext>();
  private pendingRequests = new Map<string, Promise<CollaboratorContext>>();

  async resolveContext(controlTowerId: string): Promise<CollaboratorContext> {
    const now = Date.now();
    const cached = this.cache.get(controlTowerId);

    if (cached && now - cached.timestamp < CACHE_TTL_MS) {
      return cached.context;
    }

    if (this.pendingRequests.has(controlTowerId)) {
      return this.pendingRequests.get(controlTowerId)!;
    }

    const request = this._resolve(controlTowerId, now);
    this.pendingRequests.set(controlTowerId, request);

    try {
      return await request;
    } finally {
      this.pendingRequests.delete(controlTowerId);
    }
  }

  clearCache(controlTowerId?: string) {
    if (controlTowerId) {
      this.cache.delete(controlTowerId);
    } else {
      this.cache.clear();
    }
  }

  private async _resolve(controlTowerId: string, now: number): Promise<CollaboratorContext> {
    const user = this.store.selectSnapshot(AuthState.getUser);
    if (!user) {
      return { userType: 'DEFAULT', effectiveScopes: [], collaborator: null };
    }

    try {
      const response = await firstValueFrom(
        this.collaboratorsService.getCollaboratorByUserIdAndControlTowerId(user.userId, controlTowerId)
      );
      const collaborator = response.getCollaboratorByUserIdAndControlTowerId;

      if (collaborator) {
        const context: CollaboratorContext = {
          userType: (collaborator.collaboratorType as CollaboratorContext['userType']) || 'DEFAULT',
          effectiveScopes: collaborator.scopes ?? [],
          collaborator,
          isFeatureMarketingCTACompleted: collaborator.featureMarketingMetaData?.isCTAcompleted,
        };
        this.cache.set(controlTowerId, { context, timestamp: now });
        return context;
      }

      // Not a collaborator -- fall back to org user scopes
      try {
        const scopesResponse = await firstValueFrom(
          this.collaboratorsService.findOrgUserWithScopesByIndividualId(user.individualId)
        );
        const context: CollaboratorContext = {
          userType: 'DEFAULT',
          effectiveScopes: scopesResponse.getControlTowerOrgUserWithScopesByIndividualId ?? [],
          collaborator: null,
        };
        this.cache.set(controlTowerId, { context, timestamp: now });
        return context;
      } catch {
        const context: CollaboratorContext = {
          userType: 'DEFAULT',
          effectiveScopes: user.scopes ?? [],
          collaborator: null,
        };
        this.cache.set(controlTowerId, { context, timestamp: now });
        return context;
      }
    } catch {
      const context: CollaboratorContext = {
        userType: 'DEFAULT',
        effectiveScopes: user.scopes ?? [],
        collaborator: null,
      };
      this.cache.set(controlTowerId, { context, timestamp: now });
      return context;
    }
  }
}
